import { useEffect, useState } from "react";
import { useAuth } from "@/shared/hooks/useAuth";
import { useClientSelection } from "@/features/projects/contexts/ClientSelectionContext";
import { resolveClientId } from "@/shared/lib/resolveClientId";
import { supabase } from "@/shared/integrations/supabase/client";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { isAdminUser } from "@/shared/lib/adminUids";

/**
 * Resolves the current client (a Supabase `projects` row id).
 *
 * - Regular client users: the single org from their `organization_id` claim
 *   (claim = Firebase org id = `projects.ref_id`).
 * - Admins (the 3 hardcoded UIDs): any org, chosen via the sidebar switcher
 *   (`ClientSelectionContext`), defaulting to the first org. This restores the
 *   pre-migration "switch between all clients" behavior for admins.
 */
export const useClientId = () => {
  const { user } = useAuth();
  const { selectedClientId } = useClientSelection();
  const isAdmin = isAdminUser(firebaseAuth?.currentUser?.uid);

  const [clientId, setClientId] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);

    const resolve = async () => {
      if (isAdmin) {
        // Lightweight org list (RLS is off, so admins read all projects). Honor
        // the switcher selection when valid; otherwise default to the first org.
        const { data, error } = await supabase
          .from("projects")
          .select("id, name")
          .order("name", { ascending: true });
        if (error) throw error;
        const ids = (data ?? [])
          .filter((p) => p.name !== "Unassigned")
          .map((p) => p.id);
        const id =
          selectedClientId && ids.includes(selectedClientId)
            ? selectedClientId
            : ids[0] ?? null;
        if (!cancelled) {
          setClientId(id);
          setLoading(false);
        }
        return;
      }

      const id = await resolveClientId();
      if (!cancelled) {
        setClientId(id);
        setLoading(false);
      }
    };

    resolve().catch((e) => {
      console.error("[useClientId] Failed to resolve client:", e);
      if (!cancelled) {
        setClientId(null);
        setLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [user, isAdmin, selectedClientId]);

  return { clientId, loading };
};
